// Handles the redirect target for both:
//   - Admin invite emails (auth.admin.inviteUserByEmail → set initial password)
//   - Forgot-password reset emails (resetPasswordForEmail)
//
// Supabase puts the recovery token in the URL hash; the browser client picks
// it up automatically when `detectSessionInUrl: true` (set in lib/supabase.ts).
// By the time this page renders the user is already logged in with a temporary
// recovery session — they just need to choose a new password.
//
// Stuck-state handling (added 2026-05-04):
// If the URL hash carries an explicit error (e.g. expired link), surface that
// immediately. If neither a session nor an error has materialised within 5s,
// show a generic "link is invalid or expired" with a CTA to /reset-password.
// Without this fallback the page sat on "Verifying your invite link..." with
// no escape, which is exactly what tripped Chrystal up.

import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";

/** Pull `error_description` (or fall back to `error`) out of the URL hash that
 *  Supabase appends when a magic-link verification fails — e.g.
 *    #error=access_denied&error_description=Email+link+is+invalid+or+has+expired
 *  Returns the human-readable message or null when no error is present. */
function readHashError(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const desc = params.get("error_description") ?? params.get("error");
  if (!desc) return null;
  return decodeURIComponent(desc.replace(/\+/g, " "));
}

export function SetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  // Surface any error embedded in the hash up-front. Computed once via the
  // lazy initializer (a pure read of the current URL, not an external
  // subscription) instead of synchronously inside the effect below
  // (`react-hooks/set-state-in-effect`).
  const [linkBad, setLinkBad] = useState<string | null>(() => readHashError());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Hash already carried an explicit error at mount (see the lazy
    // initializer above) — skip session polling entirely.
    if (readHashError()) return;

    // The recovery session may take a tick to land while supabase-js parses
    // the URL hash. Check once on mount and subscribe to auth-state changes.
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data.session) setHasSession(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!cancelled && session) setHasSession(true);
    });

    // 3. If 5s pass without a session AND no error showed up, treat the link
    //    as bad. This catches the "stuck on Verifying..." case where the user
    //    landed here without any token at all (e.g. opened /set-password
    //    directly, or the email link's hash was stripped by their mail client).
    const stuckTimer = window.setTimeout(() => {
      if (cancelled) return;
      supabase.auth.getSession().then(({ data }) => {
        if (cancelled) return;
        if (!data.session) {
          setLinkBad(
            "This invite link is invalid or has expired. Request a new one and try again.",
          );
        }
      });
    }, 5_000);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      window.clearTimeout(stuckTimer);
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setIsLoading(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    navigate("/dashboard");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Set your password</CardTitle>
          <CardDescription>
            {linkBad
              ? "This link can no longer be used."
              : hasSession
                ? "Pick a strong password (minimum 8 characters)."
                : "Verifying your invite link..."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {linkBad ? (
            <div className="space-y-4">
              <p className="text-sm text-destructive" role="alert">
                {linkBad}
              </p>
              <p className="text-sm text-muted-foreground">
                Magic-link tokens expire after a short window. Request a fresh
                reset link and click the new email.
              </p>
              <div className="flex flex-col gap-2">
                <Button asChild className="w-full">
                  <Link to="/reset-password">Request a new link</Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Back to sign in</Link>
                </Button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={!hasSession}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={!hasSession}
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !hasSession}
            >
              {isLoading ? "Saving..." : "Set password and sign in"}
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
